import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface EditBikeModalProps {
  bikeId: string;
  onClose: () => void;
  onBikeUpdated: () => void;
}

export function EditBikeModal({ bikeId, onClose, onBikeUpdated }: EditBikeModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bikeData, setBikeData] = useState<any>(null);

  useEffect(() => {
    fetchBikeData();
  }, [bikeId]);

  const fetchBikeData = async () => {
    try {
      const { data, error } = await supabase
        .from('bikes')
        .select(`
          *,
          current_case:cases(
            id,
            delivery_address,
            rental_start_date,
            rental_end_date
          )
        `)
        .eq('id', bikeId)
        .single();

      if (error) throw error;
      setBikeData(data);
    } catch (error) {
      console.error('Error fetching bike:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBikeData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Convert service_notes string to array by splitting on newlines and filtering empty lines
      const serviceNotesArray = bikeData.service_notes
        ? bikeData.service_notes.split('\n').filter(note => note.trim() !== '')
        : [];

      const { error } = await supabase
        .from('bikes')
        .update({
          make: bikeData.make,
          model: bikeData.model,
          rego_number: bikeData.rego_number,
          rego_expiry: bikeData.rego_expiry,
          rate_a: bikeData.rate_a,
          rate_b: bikeData.rate_b,
          last_service: bikeData.last_service,
          service_centre: bikeData.service_centre,
          service_notes: serviceNotesArray,
          status: bikeData.status
        })
        .eq('id', bikeId);

      if (error) throw error;
      onBikeUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating bike:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !bikeData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6563ff]"></div>
        </div>
      </div>
    );
  }

  // Convert array of notes to string for textarea display
  const serviceNotesString = Array.isArray(bikeData.service_notes) 
    ? bikeData.service_notes.join('\n')
    : bikeData.service_notes || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Edit Bike</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make
                </label>
                <input
                  type="text"
                  name="make"
                  value={bikeData.make || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={bikeData.model || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Number
                </label>
                <input
                  type="text"
                  name="rego_number"
                  value={bikeData.rego_number || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Expiry
                </label>
                <input
                  type="date"
                  name="rego_expiry"
                  value={bikeData.rego_expiry || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate A ($)
                </label>
                <input
                  type="number"
                  name="rate_a"
                  value={bikeData.rate_a || ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate B ($)
                </label>
                <input
                  type="number"
                  name="rate_b"
                  value={bikeData.rate_b || ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Service
                </label>
                <input
                  type="date"
                  name="last_service"
                  value={bikeData.last_service || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Centre
                </label>
                <select
                  name="service_centre"
                  value={bikeData.service_centre || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
                >
                  <option value="">Select a service centre</option>
                  <option value="five_dock">Five Dock</option>
                  <option value="seven_hills">Seven Hills</option>
                  <option value="reza">Reza</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Notes (one note per line)
            </label>
            <textarea
              name="service_notes"
              value={serviceNotesString}
              onChange={handleChange}
              rows={3}
              placeholder="Enter service notes (one per line)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
            />
          </div>

          {bikeData.current_case && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Current Assignment</h3>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Delivery Address:</span> {bikeData.current_case.delivery_address}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Rental Start:</span> {
                    bikeData.current_case.rental_start_date ? 
                    new Date(bikeData.current_case.rental_start_date).toLocaleDateString() : 
                    'Not set'
                  }
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Rental End:</span> {
                    bikeData.current_case.rental_end_date ? 
                    new Date(bikeData.current_case.rental_end_date).toLocaleDateString() : 
                    'Ongoing'
                  }
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors disabled:opacity-50"
            
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}